import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const EMPTY_FORM = { name: '', type: 'photographer', events_count: '', price_rupees: '', sort_order: '0' };

const SuperAdminPackages = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | package object
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/superadmin/packages');
      setPackages(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) logout();
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError('');
    setModal('create');
  };

  const openEdit = (pkg) => {
    setForm({
      name: pkg.name,
      type: pkg.type,
      events_count: String(pkg.events_count),
      price_rupees: String(pkg.price_paise / 100),
      sort_order: String(pkg.sort_order),
      is_active: pkg.is_active
    });
    setFormError('');
    setModal(pkg);
  };

  const handleSave = async () => {
    const { name, type, events_count, price_rupees, sort_order } = form;
    if (!name.trim()) { setFormError('Name is required.'); return; }
    if (!events_count || parseInt(events_count) < 1) { setFormError('Events count must be at least 1.'); return; }
    if (!price_rupees || parseFloat(price_rupees) <= 0) { setFormError('Price must be greater than 0.'); return; }

    setSaving(true); setFormError('');
    const payload = {
      name: name.trim(),
      type,
      events_count: parseInt(events_count),
      price_paise: Math.round(parseFloat(price_rupees) * 100),
      sort_order: parseInt(sort_order) || 0,
      is_active: form.is_active !== false
    };

    try {
      if (modal === 'create') {
        const res = await axios.post('/api/superadmin/packages', payload);
        setPackages(prev => [...prev, res.data]);
      } else {
        const res = await axios.put(`/api/superadmin/packages/${modal.id}`, payload);
        setPackages(prev => prev.map(p => p.id === modal.id ? res.data : p));
      }
      setModal(null);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Save failed.');
    } finally { setSaving(false); }
  };

  const toggleActive = async (pkg) => {
    try {
      const res = await axios.put(`/api/superadmin/packages/${pkg.id}`, {
        name: pkg.name, type: pkg.type, events_count: pkg.events_count,
        price_paise: pkg.price_paise, sort_order: pkg.sort_order,
        is_active: !pkg.is_active
      });
      setPackages(prev => prev.map(p => p.id === pkg.id ? res.data : p));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed.');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`/api/superadmin/packages/${deleteTarget.id}`);
      setPackages(prev => prev.filter(p => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed.');
    } finally { setDeleteLoading(false); }
  };

  const photographer = packages.filter(p => p.type === 'photographer');
  const dealer = packages.filter(p => p.type === 'dealer');

  return (
    <div style={s.page}>
      <div style={s.container}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Packages</h1>
            <p style={s.subtitle}>Manage event credit packages sold to photographers and dealers</p>
          </div>
          <button style={s.createBtn} onClick={openCreate}>+ New Package</button>
        </div>

        {loading ? (
          <p style={{ color: '#888', textAlign: 'center', padding: '3rem' }}>Loading...</p>
        ) : (
          <>
            <PackageSection
              title="Photographer Packages"
              subtitle="Sold directly to photographers"
              packages={photographer}
              onEdit={openEdit}
              onToggle={toggleActive}
              onDelete={setDeleteTarget}
            />
            <PackageSection
              title="Dealer Packages"
              subtitle="Bulk packages sold to dealers"
              packages={dealer}
              onEdit={openEdit}
              onToggle={toggleActive}
              onDelete={setDeleteTarget}
            />
          </>
        )}
      </div>

      {/* Create / Edit Modal */}
      {modal && (
        <div style={s.overlay} onClick={() => setModal(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h2 style={s.modalTitle}>{modal === 'create' ? 'New Package' : 'Edit Package'}</h2>

            <Field label="Package Name *">
              <input style={s.input} value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Starter Pack" />
            </Field>

            <Field label="For *">
              <div style={s.segmented}>
                {['photographer', 'dealer'].map(t => (
                  <button key={t} style={{ ...s.segBtn, ...(form.type === t ? s.segBtnActive : {}) }}
                    onClick={() => setForm(f => ({ ...f, type: t }))}>
                    {t === 'photographer' ? '📸 Photographer' : '🤝 Dealer'}
                  </button>
                ))}
              </div>
            </Field>

            <div style={s.row}>
              <Field label="Events Count *">
                <input style={s.input} type="number" min="1" value={form.events_count}
                  onChange={e => setForm(f => ({ ...f, events_count: e.target.value }))}
                  placeholder="e.g. 5" />
              </Field>
              <Field label="Price (₹) *">
                <input style={s.input} type="number" min="1" step="0.01" value={form.price_rupees}
                  onChange={e => setForm(f => ({ ...f, price_rupees: e.target.value }))}
                  placeholder="e.g. 499" />
              </Field>
            </div>

            <Field label="Sort Order">
              <input style={s.input} type="number" min="0" value={form.sort_order}
                onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
                placeholder="0 = first" />
              <p style={s.hint}>Lower number = shown first in the list.</p>
            </Field>

            {modal !== 'create' && (
              <Field label="Status">
                <div style={s.segmented}>
                  {[true, false].map(v => (
                    <button key={String(v)}
                      style={{ ...s.segBtn, ...(form.is_active === v ? s.segBtnActive : {}) }}
                      onClick={() => setForm(f => ({ ...f, is_active: v }))}>
                      {v ? '✅ Active' : '⏸ Inactive'}
                    </button>
                  ))}
                </div>
              </Field>
            )}

            {formError && <div style={s.error}>{formError}</div>}

            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={() => setModal(null)}>Cancel</button>
              <button style={{ ...s.submitBtn, ...(saving ? s.disabled : {}) }} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : modal === 'create' ? 'Create Package' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div style={s.overlay} onClick={() => setDeleteTarget(null)}>
          <div style={{ ...s.modal, maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <h2 style={s.modalTitle}>Delete Package?</h2>
            <p style={{ color: '#555', fontSize: '0.9rem', margin: '0 0 0.5rem' }}>
              <strong>{deleteTarget.name}</strong>
            </p>
            <p style={{ color: '#888', fontSize: '0.85rem', margin: '0 0 1.5rem' }}>
              This cannot be undone. Existing orders referencing this package are not affected.
              Consider deactivating instead.
            </p>
            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button style={{ ...s.submitBtn, background: '#dc2626', ...(deleteLoading ? s.disabled : {}) }}
                onClick={handleDelete} disabled={deleteLoading}>
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PackageSection = ({ title, subtitle, packages, onEdit, onToggle, onDelete }) => (
  <div style={s.section}>
    <div style={s.sectionHeader}>
      <div>
        <h2 style={s.sectionTitle}>{title}</h2>
        <p style={s.sectionSubtitle}>{subtitle}</p>
      </div>
      <span style={s.countBadge}>{packages.length} packages</span>
    </div>
    {packages.length === 0 ? (
      <p style={s.empty}>No packages yet.</p>
    ) : (
      <div style={s.grid}>
        {packages.map(pkg => (
          <div key={pkg.id} style={{ ...s.card, ...(!pkg.is_active ? s.cardInactive : {}) }}>
            <div style={s.cardTop}>
              <div style={s.pkgName}>{pkg.name}</div>
              <span style={{ ...s.badge, ...(pkg.is_active ? s.badgeActive : s.badgeInactive) }}>
                {pkg.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div style={s.pkgStats}>
              <div style={s.stat}>
                <span style={s.statVal}>{pkg.events_count}</span>
                <span style={s.statLabel}>events</span>
              </div>
              <div style={s.statDivider} />
              <div style={s.stat}>
                <span style={s.statVal}>₹{(pkg.price_paise / 100).toLocaleString('en-IN')}</span>
                <span style={s.statLabel}>price</span>
              </div>
              <div style={s.statDivider} />
              <div style={s.stat}>
                <span style={s.statVal}>₹{Math.round(pkg.price_paise / pkg.events_count / 100)}</span>
                <span style={s.statLabel}>per event</span>
              </div>
            </div>
            <div style={s.cardActions}>
              <button style={s.editBtn} onClick={() => onEdit(pkg)}>Edit</button>
              <button style={{ ...s.toggleBtn, color: pkg.is_active ? '#854d0e' : '#166534' }}
                onClick={() => onToggle(pkg)}>
                {pkg.is_active ? 'Deactivate' : 'Activate'}
              </button>
              <button style={s.deleteBtn} onClick={() => onDelete(pkg)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const Field = ({ label, children }) => (
  <div style={{ marginBottom: '1rem' }}>
    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>{label}</label>
    {children}
  </div>
);

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', system-ui, sans-serif" },
  nav: { background: '#1a1a2e', padding: '1rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem' },
  navBrand: { color: '#fff', fontWeight: '700', fontSize: '1.1rem', flex: 1 },
  navLinks: { display: 'flex', gap: '0.5rem' },
  navBtn: { background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.875rem', cursor: 'pointer', fontSize: '0.875rem' },
  logoutBtn: { background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.875rem', cursor: 'pointer', fontSize: '0.875rem' },
  container: { maxWidth: '1100px', margin: '0 auto', padding: '2rem 1.5rem' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '2rem', gap: '1rem' },
  title: { fontSize: '1.75rem', fontWeight: '800', color: '#1a1a2e', margin: '0 0 0.25rem' },
  subtitle: { color: '#888', margin: 0, fontSize: '0.9rem' },
  createBtn: { background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '0.75rem', padding: '0.7rem 1.25rem', fontWeight: '700', cursor: 'pointer', fontSize: '0.95rem', whiteSpace: 'nowrap' },
  section: { marginBottom: '2.5rem' },
  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' },
  sectionTitle: { fontSize: '1.1rem', fontWeight: '800', color: '#1a1a2e', margin: '0 0 0.15rem' },
  sectionSubtitle: { color: '#aaa', fontSize: '0.8rem', margin: 0 },
  countBadge: { background: '#f3f4f6', color: '#555', borderRadius: '50px', padding: '0.2rem 0.75rem', fontSize: '0.8rem', fontWeight: '700' },
  empty: { color: '#aaa', background: '#fff', borderRadius: '0.75rem', padding: '1.5rem', textAlign: 'center', border: '1.5px dashed #e5e7eb' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' },
  card: { background: '#fff', borderRadius: '1rem', padding: '1.25rem', border: '1.5px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  cardInactive: { opacity: 0.6 },
  cardTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '1rem' },
  pkgName: { fontWeight: '700', color: '#1a1a2e', fontSize: '1rem' },
  badge: { borderRadius: '50px', padding: '0.2rem 0.65rem', fontSize: '0.72rem', fontWeight: '700', whiteSpace: 'nowrap' },
  badgeActive: { background: '#f0fdf4', color: '#166534' },
  badgeInactive: { background: '#f3f4f6', color: '#888' },
  pkgStats: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', background: '#f8fafc', borderRadius: '0.5rem', padding: '0.75rem' },
  stat: { flex: 1, textAlign: 'center' },
  statVal: { display: 'block', fontWeight: '800', color: '#1a1a2e', fontSize: '1.05rem' },
  statLabel: { fontSize: '0.7rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.04em' },
  statDivider: { width: '1px', height: '30px', background: '#e5e7eb' },
  cardActions: { display: 'flex', gap: '0.5rem' },
  editBtn: { flex: 1, padding: '0.45rem', background: '#f0f4ff', color: '#4f46e5', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' },
  toggleBtn: { flex: 1, padding: '0.45rem', background: '#fffbeb', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' },
  deleteBtn: { padding: '0.45rem 0.75rem', background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' },
  modal: { background: '#fff', borderRadius: '1.25rem', padding: '2rem', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: '1.3rem', fontWeight: '800', color: '#1a1a2e', margin: '0 0 1.25rem' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' },
  input: { width: '100%', padding: '0.75rem 1rem', border: '1.5px solid #e5e7eb', borderRadius: '0.75rem', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' },
  segmented: { display: 'flex', gap: '0.5rem' },
  segBtn: { flex: 1, padding: '0.6rem', background: '#f3f4f6', border: '1.5px solid #e5e7eb', borderRadius: '0.6rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem', color: '#555' },
  segBtnActive: { background: '#1a1a2e', color: '#fff', borderColor: '#1a1a2e' },
  hint: { margin: '0.3rem 0 0', fontSize: '0.78rem', color: '#aaa' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '0.75rem', color: '#dc2626', fontSize: '0.875rem', marginBottom: '0.5rem' },
  modalActions: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.25rem' },
  cancelBtn: { padding: '0.7rem 1.25rem', background: '#f3f4f6', border: 'none', borderRadius: '0.75rem', cursor: 'pointer', fontWeight: '600', color: '#444' },
  submitBtn: { padding: '0.7rem 1.5rem', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '0.75rem', fontWeight: '700', cursor: 'pointer' },
  disabled: { opacity: 0.6, cursor: 'not-allowed' }
};

export default SuperAdminPackages;
